import { useCallback, useEffect, useState } from 'react'
import { Slot } from '@medplum/fhirtypes'
import { Button, Stack, Card, Group, Title, Text, Divider, LoadingOverlay } from '@mantine/core'
import { IconArrowLeft } from '@tabler/icons-react'
import { useMiddlewareClient } from '@/hooks'
import { logError } from '@/utils/errorUtils'

type AvailableSlotsResponse = {
  success: boolean
  slots: Slot[]
}

interface SlotsGridProps {
  date: Date
  onSlotClick: (slot: Slot) => void
  onBack: () => void
}

const SlotsGrid = ({ date, onSlotClick, onBack }: SlotsGridProps) => {
  const middleware = useMiddlewareClient()
  const [slotsForDate, setSlotsForDate] = useState<Slot[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const now = Date.now()
  const fetchSlots = useCallback(async () => {
    setIsLoading(true)
    try {
      const day = String(new Date(date).getDate()).padStart(2, '0')
      const month = String(new Date(date).getMonth() + 1).padStart(2, '0')
      const year = new Date(date).getFullYear()
      const formattedDate = `${year}-${month}-${day}`
      const result = (await middleware.get('/appointment/slots', {
        day: formattedDate,
      })) as AvailableSlotsResponse
      const slotsRes = result.slots ?? []
      if (slotsRes.length > 0) {
        // TODO: SPLIT GRID after decision on handling duplicate time slots
        const removedTimeDuplicates = [
          ...new Map(slotsRes.map((item) => [item.start, item])).values(),
        ]
        setSlotsForDate(removedTimeDuplicates)
      } else {
        setSlotsForDate([])
      }
    } catch (error) {
      logError('call-center', 'Error fetching slots for date', error)
    } finally {
      setIsLoading(false)
    }
  }, [date, middleware])
  useEffect(() => {
    if (date) {
      fetchSlots()
    }
  }, [date, fetchSlots])
  return (
    <Card
      radius="xl"
      p="xl"
      withBorder
      style={{
        width: 460,
        minHeight: 300,
        margin: '0 auto',
        borderColor: 'rgba(34, 139, 230, 0.18)',
        background:
          'radial-gradient(1200px 600px at 20% -10%, rgba(34,139,230,0.14) 0%, transparent 55%),' +
          'radial-gradient(900px 500px at 110% 10%, rgba(99,102,241,0.10) 0%, transparent 55%),' +
          'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.98) 100%)',
        boxShadow: '0 20px 45px rgba(0,0,0,0.08), 0 2px 10px rgba(0,0,0,0.05)',
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={2} style={{ lineHeight: 1.1 }}>
            Select a Time Slot
          </Title>
          <Text c="dimmed" size="sm" style={{ textAlign: 'left' }}>
            All times shown in EST
          </Text>
        </Stack>
      </Group>
      <Divider my="lg" />
      <LoadingOverlay visible={isLoading} />
      <div className="mt-4 grid grid-cols-4 gap-2">
        {slotsForDate.map((s) => {
          const slotStart = new Date(s.start)
          return (
            slotStart.getTime() > now &&
            slotStart.getTime() > date.getTime() &&
            slotStart.getTime() < date.getTime() + 24 * 3600 * 1000 && (
              <div key={s.id} className="">
                <Button
                  variant="outline"
                  className="!bg-[rgba(101, 162, 227, 1)] !w-[80px] !px-[5px] !text-[13px]"
                  onClick={() => onSlotClick(s)}
                >
                  {slotStart
                    .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
                    .toLocaleUpperCase()}
                </Button>
              </div>
            )
          )
        })}
      </div>
      <Divider my="lg" />
      <Group justify="flex-start" align="flex-start" mt="md">
        <Button variant="outline" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          Back
        </Button>
      </Group>
    </Card>
  )
}

export default SlotsGrid
